/**
 * Full-screen sign-in. Shown when `useAuth().user === null`.
 *
 * Visually, the login UI is positioned to render *inside* the dark TV
 * plate on the back wall of the operations-room photo. All elements
 * (title, plasma orb, form, status row) live inside that plate so the
 * page reads as "the terminal on the screen is the sign-in", not as
 * a panel floating in front of the room.
 *
 * The TV plate sits at roughly 31-69 % horizontally × 20-50 %
 * vertically of the 2200 × 1257 `bg-mission-2.png` photo (same
 * operations-room scene Mission Control uses, foreground monitor row
 * removed — matches MC-7). With the photo rendered `bg-cover
 * bg-center`, that lands as a centred ~38 %-wide ×
 * ~30 %-tall band across common viewport ratios — which is what
 * the overlay below claims with `top: 18%` and `width: min(640px,
 * 38vw)`. The numbers are tuned for a 16:9 viewport; narrower
 * viewports get a slightly different fit but the percentages still
 * place the form inside the dark zone of the cropped photo.
 *
 * Title and orb come from `<XaesEntryShell>` in their compact size so
 * they share the visual language of the Mission Control opening
 * screen without competing with the form for vertical space.
 */

import { useEffect, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import bgMission from "@/assets/bg-mission-2.png";
import { PlasmaOrb, XaesTitle } from "@/components/shell/XaesEntryShell";
import { login as content } from "@/content/ui";
import { warmUpBff, warmUpIdp } from "@/features/auth/warm-up";
import { useAuth } from "@/hooks/use-auth";

export function LoginPage() {
  const { login, isManagedExternally } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Wake Zitadel + the BFF while the user is still on this screen so their
  // cold starts overlap with think-time instead of stacking after the click.
  useEffect(() => {
    warmUpIdp();
    warmUpBff();
  }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isManagedExternally) {
      // ZITADEL mode — login() kicks off signinRedirect() and the browser
      // navigates away. Set submitting=true so the button reads as busy
      // for the few hundred ms before the redirect fires.
      setSubmitting(true);
      login("", "");
      return;
    }
    const ok = login(username.trim().toLowerCase(), password);
    if (!ok) {
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  }

  return (
    <div className="bg-background relative flex h-full flex-col overflow-hidden">
      {/* Operations-room hero photo, edge-to-edge. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgMission})` }}
      />

      {/* Monitor signal acquire — a single 2 px sky-blue line races
          from the top of the viewport to the bottom on first mount, as
          if the screen is painting its first raster line. Pure CSS,
          no JS state; the animation runs once via `forwards`. */}
      <div
        aria-hidden
        data-testid="boot-scan-line"
        className="pointer-events-none absolute inset-x-0 top-0 z-20"
        style={{
          height: 2,
          background:
            "linear-gradient(90deg, transparent, rgba(var(--accent-sky-rgb), 0.95) 20%, rgba(255,255,255,0.85) 50%, rgba(var(--accent-sky-rgb), 0.95) 80%, transparent)",
          boxShadow:
            "0 0 18px rgba(var(--accent-sky-rgb), 0.6), 0 0 36px rgba(var(--accent-true-blue-rgb), 0.4)",
          animation:
            "signal-scan-down 1.4s cubic-bezier(0.22, 1, 0.36, 1) forwards",
          willChange: "transform, opacity",
        }}
      />

      {/* Ambient flicker layer — barely-there opacity wobble that
          makes the photo feel like it's behind a powered display. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20 mix-blend-overlay"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(var(--accent-sky-rgb), 0.08), transparent 70%)",
          animation: "monitor-flicker 4.8s ease-in-out infinite",
        }}
      />

      {/* TV-plate overlay. Anchored to the *centre* of the dark screen
          on the back wall of the operations-room photo, not the top.
          With the photo at `bg-cover bg-center`, the TV's centre lands
          at roughly (50 %, 33 %) of the viewport across every common
          aspect ratio (16:9, 21:9, 4:3). Centring the overlay there
          with `translate(-50%, -50%)` keeps the title, orb, and form
          all inside the plate as the viewport scales. */}
      <div className="login-plate absolute z-10 flex flex-col items-center">
        <XaesTitle size="compact" />
        <div className="login-orb">
          <PlasmaOrb size="compact" />
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex w-full flex-col items-stretch"
          autoComplete="on"
          style={{
            maxWidth: 360,
            marginTop: "0.4vw",
            gap: 8,
          }}
        >
          {/* Eyebrow — terminal status row above the inputs. */}
          <div className="flex items-center justify-center" style={{ gap: 8 }}>
            <span
              aria-hidden
              className="inline-block animate-pulse rounded-full"
              style={{
                width: 6,
                height: 6,
                background: "var(--accent-green)",
                boxShadow: "var(--glow-green)",
              }}
            />
            <span
              className="font-mono uppercase"
              style={{
                fontSize: "var(--fs-label)",
                letterSpacing: "0.28em",
                color: "rgba(var(--accent-sky-rgb), 0.85)",
                textShadow:
                  "0 0 12px rgba(var(--accent-true-blue-rgb), 0.3)",
              }}
            >
              {content.subtitle}
            </span>
          </div>

          {/* Credential inputs only render in seed mode. ZITADEL mode
              delegates auth to the hosted Login UI on :3000, so we don't
              want a faux username/password form here that doesn't do
              anything. The eyebrow + button + footer chrome are shared. */}
          {!isManagedExternally && (
            <motion.div
              animate={shaking ? { x: [-8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
              transition={{ duration: 0.45 }}
              className="flex flex-col"
              style={{ gap: 6 }}
            >
              <TerminalInput
                type="text"
                value={username}
                onChange={(v) => {
                  setUsername(v);
                  setError(false);
                }}
                placeholder={content.usernamePlaceholder}
                autoComplete="username"
                ariaLabel="Operator"
                error={error}
              />
              <TerminalInput
                type="password"
                value={password}
                onChange={(v) => {
                  setPassword(v);
                  setError(false);
                }}
                placeholder={content.passwordPlaceholder}
                autoComplete="current-password"
                ariaLabel="Access code"
                error={error}
              />
            </motion.div>
          )}

          {/* Fixed slot for the error row so the layout doesn't jump. */}
          <div
            aria-live="polite"
            className="flex items-center justify-center"
            style={{ minHeight: 16 }}
          >
            <AnimatePresence>
              {error && (
                <motion.span
                  key="error"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="font-mono uppercase"
                  style={{
                    color: "var(--accent-red)",
                    fontSize: "var(--fs-micro)",
                    letterSpacing: "0.18em",
                  }}
                  role="alert"
                >
                  {content.errorMessage}
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          <motion.button
            type="submit"
            disabled={submitting}
            whileTap={{ scale: 0.97 }}
            className="font-mono font-bold uppercase transition-all"
            style={{
              padding: isManagedExternally ? "12px 0" : "8px 0",
              background: "rgba(var(--accent-true-blue-rgb), 0.10)",
              border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
              borderRadius: 6,
              color: "var(--accent-sky)",
              // Match Loveable v1 (~11px) and step above the 10px label
              // floor so the ZITADEL CTA remains legible on the TV plate.
              fontSize: "var(--fs-sub)",
              letterSpacing: "0.28em",
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.7 : 1,
              boxShadow:
                "0 0 14px rgba(var(--accent-true-blue-rgb), 0.12), inset 0 0 12px rgba(var(--accent-true-blue-rgb), 0.05)",
            }}
            onMouseEnter={(e) => {
              if (submitting) return;
              e.currentTarget.style.background =
                "rgba(var(--accent-true-blue-rgb), 0.18)";
              e.currentTarget.style.boxShadow =
                "0 0 26px rgba(var(--accent-true-blue-rgb), 0.28), inset 0 0 12px rgba(var(--accent-true-blue-rgb), 0.10)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "rgba(var(--accent-true-blue-rgb), 0.10)";
              e.currentTarget.style.boxShadow =
                "0 0 14px rgba(var(--accent-true-blue-rgb), 0.12), inset 0 0 12px rgba(var(--accent-true-blue-rgb), 0.05)";
            }}
          >
            {isManagedExternally
              ? submitting
                ? content.ssoBusyLabel
                : content.ssoSubmitLabel
              : content.submitLabel}
          </motion.button>

          {/* Affordance line — only shown in ZITADEL mode so users know
              they're about to leave the SPA for the hosted Login UI. */}
          {isManagedExternally && (
            <p
              className="text-center font-mono"
              style={{
                marginTop: 6,
                color: "rgba(var(--accent-sky-rgb), 0.55)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.14em",
              }}
            >
              {content.ssoHint}
            </p>
          )}
        </motion.form>
      </div>

      {/* Bar-btm status strip lives outside the TV plate so the
          screen-content inside stays clean. */}
      <footer
        className="login-footer relative z-10 mt-auto flex min-w-0 flex-shrink-0 items-center justify-between gap-3"
        style={{
          padding: "10px 18px",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.0))",
        }}
      >
        <span
          className="min-w-0 truncate font-mono uppercase"
          style={{
            color: "rgba(var(--accent-true-blue-rgb), 0.65)",
            fontSize: "var(--fs-micro)",
            letterSpacing: "0.32em",
          }}
        >
          {content.title}
        </span>
        <motion.span
          animate={{ opacity: [0.4, 0.95, 0.4] }}
          transition={{ duration: 2.2, repeat: Infinity }}
          className="login-footer-status min-w-0 shrink truncate font-mono uppercase"
          style={{
            color: "var(--accent-sky)",
            fontSize: "var(--fs-micro)",
            letterSpacing: "0.32em",
          }}
        >
          {content.statusLine}
        </motion.span>
      </footer>
    </div>
  );
}

/**
 * Terminal-style text field. No card chrome, no per-field label.
 * Tinted true-blue line with a soft focus glow and an error-state
 * border tint, sized to fit two stacked fields plus a button inside
 * the TV plate.
 */
function TerminalInput({
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  ariaLabel,
  error,
}: {
  type: "text" | "password";
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  autoComplete: string;
  ariaLabel: string;
  error: boolean;
}) {
  const baseBorder = error
    ? "rgba(var(--accent-red-rgb), 0.55)"
    : "rgba(var(--accent-true-blue-rgb), 0.30)";
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-label={ariaLabel}
      spellCheck={false}
      // No `uppercase` text-transform: previous build force-rendered
      // every keystroke in caps, which made it impossible to see if
      // caps-lock was on. Passwords are case-sensitive, so users were
      // typing "XAES-FACILITATE" while seeing "xaes-facilitate". The
      // input now renders the exact characters submitted to `login()`.
      className="w-full font-mono outline-none transition-all"
      style={{
        padding: "10px 12px",
        background: "rgba(0, 0, 0, 0.40)",
        border: `1px solid ${baseBorder}`,
        borderRadius: 6,
        color: "var(--text-primary)",
        fontSize: "var(--fs-base)",
        letterSpacing: "0.08em",
        boxSizing: "border-box",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor =
          "rgba(var(--accent-sky-rgb), 0.65)";
        e.currentTarget.style.boxShadow =
          "0 0 0 3px rgba(var(--accent-true-blue-rgb), 0.10), 0 0 18px rgba(var(--accent-true-blue-rgb), 0.18)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = baseBorder;
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  );
}
